import { Inject, Injectable } from '@angular/core';
import { HelperService } from '../public/helper.service';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ViewportService {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private helperService: HelperService,
  ) {}

  applyCustomViewportForIOS() {
    if (this.helperService.isIOS()) {
      this.removeViewportMetaTag();
      this.createViewportMetaTag(
        'width=device-width, initial-scale=1.0, user-scalable=0',
      );
    }
  }

  /**
   * Since targetSdk 35, Android no longer resizes the WebView window when the
   * soft keyboard opens; the default `resizes-visual` behavior then shrinks
   * only the visual viewport while `position: fixed` elements stay attached to
   * the layout viewport. The divergence can survive the keyboard's dismissal
   * (login screen → My Account), leaving the fixed bottom bar displaced below
   * the visible area. `interactive-widget=resizes-content` restores the
   * pre-Android-15 resize behavior every `calc(var(--vh) * 100)` layout was
   * built against. The existing meta is amended in place: replacing it here
   * would race the iOS path above, and a second viewport tag is UA-order
   * dependent.
   */
  applyResizesContentViewportForAndroidTemplateApp(): void {
    if (
      !this.helperService.isGoTemplateApp() ||
      !this.helperService.isAndroid()
    ) {
      return;
    }
    const meta = this.document.head.querySelector('meta[name=viewport]');
    if (!meta) {
      this.createViewportMetaTag(
        'width=device-width, initial-scale=1, interactive-widget=resizes-content',
      );
      return;
    }
    const content = meta.getAttribute('content') || '';
    if (!content.includes('interactive-widget')) {
      meta.setAttribute(
        'content',
        `${content}, interactive-widget=resizes-content`,
      );
    }
  }

  private createViewportMetaTag(content: string): void {
    const meta = this.document.createElement('meta');
    meta.setAttribute('name', 'viewport');
    meta.setAttribute('content', content);
    this.document.head.appendChild(meta);
  }

  private removeViewportMetaTag(): void {
    const existingMetaTag = this.document.head.querySelector(
      'meta[name=viewport]',
    );
    if (existingMetaTag) {
      this.document.head.removeChild(existingMetaTag);
    }
  }
}
